// src/components/manager-v2/TeamApprovals.js
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, FileText, CheckCircle, XCircle, Clock, AlertCircle, Loader, DollarSign, Download, Eye } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useNotifications } from '../../contexts/NotificationContext';
import leaveService from '../../services/leave.service';
import attendanceService from '../../services/attendance.service';
import flexiService from '../../services/flexi.service';
import approvalService from '../../services/approval.service';
import { formatSubmissionTime, formatDate } from '../../utils/dateUtils';
import { formatTime, toISTDateString } from '../../utils/dateHelpers';

const TeamApprovals = () => {
    const { user } = useAuth();
    const { employees } = useEmployees();
    const { addNotification } = useNotifications();
    const [activeTab, setActiveTab] = useState('leave'); // 'leave', 'attendance', 'flexi'
    const tabLocation = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(tabLocation.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
    }, [tabLocation.search]);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [attendanceRequests, setAttendanceRequests] = useState([]);
    const [flexiRequests, setFlexiRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null); // Track which request is being processed
    const [error, setError] = useState(null);
    const [pendingCounts, setPendingCounts] = useState({ leave: 0, attendance: 0, flexi: 0 });

    // Get team members
    const teamMembers = employees.filter(emp => emp.managerId === user?.id || emp.reporting_manager_id === user?.id);

    // Fetch leave requests
    const fetchLeaveRequests = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await leaveService.getAllLeaveRequests();

            // Filter only pending requests
            const pendingRequests = (response.requests || []).filter(req => req.status === 'pending');
            setLeaveRequests(pendingRequests);
        } catch (err) {
            console.error('Error fetching leave requests:', err);
            setError('Failed to load leave requests');
            setLeaveRequests([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch attendance regularization requests
    const fetchAttendanceRequests = async () => {
        try {
            setLoading(true);
            setError(null);
            console.log('🔍 Fetching team attendance regularization requests...');

            const [regularizationResponse, wfhResponse] = await Promise.all([
                attendanceService.getTeamRegularizationRequests(),
                attendanceService.getTeamWfhRequests()
            ]);

            console.log('✅ Regularization requests response:', regularizationResponse);
            console.log('✅ WFH requests response:', wfhResponse);

            // Handle different response structures for regularizations
            let regularizationRequests = [];
            if (regularizationResponse.data && Array.isArray(regularizationResponse.data.requests)) {
                regularizationRequests = regularizationResponse.data.requests;
            } else if (regularizationResponse.data && Array.isArray(regularizationResponse.data)) {
                regularizationRequests = regularizationResponse.data;
            } else if (regularizationResponse.requests && Array.isArray(regularizationResponse.requests)) {
                regularizationRequests = regularizationResponse.requests;
            } else if (regularizationResponse.data && regularizationResponse.data.data && Array.isArray(regularizationResponse.data.data.requests)) {
                regularizationRequests = regularizationResponse.data.data.requests;
            }

            // Handle WFH requests
            const wfhRequests = wfhResponse?.requests || wfhResponse || [];

            // Combine both types with type markers
            const allRequests = [
                ...regularizationRequests.map(req => ({ ...req, type: 'regularization' })),
                ...wfhRequests.map(req => ({ ...req, type: 'wfh' }))
            ];

            console.log('🔢 Total requests found:', allRequests.length);
            console.log('📋 Request details:', allRequests);

            // Filter only pending requests
            const pendingRequests = allRequests.filter(req => req.status === 'pending');
            console.log('⏳ Pending requests:', pendingRequests);

            setAttendanceRequests(pendingRequests);
        } catch (err) {
            console.error('❌ Error fetching attendance requests:', err);
            console.error('❌ Error details:', err.response?.data);
            setError('Failed to load attendance requests');
            setAttendanceRequests([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch flexi component requests
    const fetchFlexiRequests = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await flexiService.getTeamRequests();

            // Filter only pending and under_review requests
            const pendingRequests = (response.requests || []).filter(req =>
                req.status === 'pending' || req.status === 'under_review'
            );
            setFlexiRequests(pendingRequests);
        } catch (err) {
            console.error('Error fetching flexi requests:', err);
            setError('Failed to load flexi component requests');
            setFlexiRequests([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch pending counts for tab badges
    const fetchPendingCounts = useCallback(async () => {
        try {
            const response = await approvalService.getPendingApprovalsCount();
            if (response.success) {
                setPendingCounts({
                    leave: response.leave || 0,
                    attendance: response.attendance || 0,
                    flexi: flexiRequests.length // Keep client-side count for flexi
                });
            }
        } catch (err) {
            console.error('Error fetching pending counts:', err);
        }
    }, [flexiRequests.length]);

    // Fetch pending counts on mount
    useEffect(() => {
        fetchPendingCounts();
    }, [fetchPendingCounts]);

    // Initial load
    useEffect(() => {
        if (activeTab === 'leave') {
            fetchLeaveRequests();
        } else if (activeTab === 'attendance') {
            fetchAttendanceRequests();
        } else if (activeTab === 'flexi') {
            fetchFlexiRequests();
        }
    }, [activeTab]);

    const handleApproveLeave = async (requestId) => {
        try {
            setProcessing(requestId);

            await leaveService.approveLeave(requestId);

            // Backend sends notification to employee automatically

            // Show success message and refresh list
            alert('Leave request approved successfully');
            await fetchLeaveRequests();
            await fetchPendingCounts();
        } catch (err) {
            console.error('Error approving leave:', err);
            alert(err.response?.data?.error || 'Failed to approve leave request');
        } finally {
            setProcessing(null);
        }
    };

    const handleRejectLeave = async (requestId) => {
        const reason = prompt('Please provide a reason for rejection:');
        if (!reason) return;

        try {
            setProcessing(requestId);

            await leaveService.rejectLeave(requestId, reason);

            // Backend sends notification to employee automatically

            // Show success message and refresh list
            alert('Leave request rejected');
            await fetchLeaveRequests();
            await fetchPendingCounts();
        } catch (err) {
            console.error('Error rejecting leave:', err);
            alert(err.response?.data?.error || 'Failed to reject leave request');
        } finally {
            setProcessing(null);
        }
    };

    const handleApproveAttendance = async (request) => {
        try {
            setProcessing(request.id);

            if (request.type === 'wfh') {
                // Handle WFH request approval
                await attendanceService.processWfhRequest(request.id, 'approve');

                // Backend sends notification to employee automatically

                alert('Work from home request approved successfully');
            } else {
                // Handle regularization request approval
                // Check context to see if we need to show decision options
                let decisionType = 'regular';
                if (request.requested_punch_in && request.requested_punch_out) {
                    try {
                        // Extract just time from timestamps (HH:MM format) in IST
                        const punchInTime = request.requested_punch_in.includes('T')
                            ? new Date(request.requested_punch_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' })
                            : request.requested_punch_in.split(' ')[1]?.substring(0, 5);
                        const punchOutTime = request.requested_punch_out.includes('T')
                            ? new Date(request.requested_punch_out).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' })
                            : request.requested_punch_out.split(' ')[1]?.substring(0, 5);

                        const context = await attendanceService.checkRegularizationContext(
                            request.employee_id,
                            request.attendance_date,
                            punchInTime,
                            punchOutTime
                        );

                        // Show decision options if needed
                        if (context.context?.showCompOffOption || context.context?.showOvertimeOption) {
                            let message = `Approve this regularization request?\n\n`;
                            message += `Date: ${formatDate(request.attendance_date)}\n`;
                            message += `Work Hours: ${context.context.workHours} hours\n`;

                            if (context.context.isNonWorkingDay) {
                                message += `⚠️ This is a ${context.context.nonWorkingDayType}: ${context.context.nonWorkingDayDetails}\n\n`;
                            } else if (context.context.isOvertime) {
                                message += `⚠️ Work hours exceed 8 hours (overtime)\n\n`;
                            }

                            message += `\nChoose approval type:\n`;
                            message += `1 - Regular attendance\n`;
                            if (context.context.showCompOffOption) {
                                message += `2 - Comp-off (award compensatory leave)\n`;
                            }
                            if (context.context.showOvertimeOption) {
                                message += `3 - Overtime (mark as overtime)\n`;
                            }

                            const choice = prompt(message, context.context.showCompOffOption ? '2' : (context.context.showOvertimeOption ? '3' : '1'));

                            if (!choice) {
                                setProcessing(null);
                                return; // User cancelled
                            }

                            if (choice === '2' && context.context.showCompOffOption) {
                                decisionType = 'comp_off';
                            } else if (choice === '3' && context.context.showOvertimeOption) {
                                decisionType = 'overtime';
                            }
                        }
                    } catch (err) {
                        console.error('Error checking context:', err);
                        // Continue with regular approval
                    }
                }

                await attendanceService.processRegularizationRequest(request.id, 'approve', null, decisionType);

                // Backend sends notification to employee automatically

                // Show success message and refresh list
                const typeLabel = decisionType === 'comp_off' ? ' with comp-off' : (decisionType === 'overtime' ? ' as overtime' : '');
                alert(`Attendance regularization approved successfully${typeLabel}`);
            }

            await fetchAttendanceRequests();
            await fetchPendingCounts();
        } catch (err) {
            console.error('Error approving attendance:', err);
            alert(err.response?.data?.error || 'Failed to approve attendance request');
        } finally {
            setProcessing(null);
        }
    };

    const handleRejectAttendance = async (request) => {
        const comments = prompt('Please provide a reason for rejection:');
        if (!comments) return;

        try {
            setProcessing(request.id);

            if (request.type === 'wfh') {
                // Handle WFH request rejection
                await attendanceService.processWfhRequest(request.id, 'reject', comments);

                // Backend sends notification to employee automatically

                alert('Work from home request rejected');
            } else {
                // Handle regularization request rejection
                await attendanceService.processRegularizationRequest(request.id, 'reject', comments);

                // Backend sends notification to employee automatically

                alert('Attendance regularization rejected');
            }

            await fetchAttendanceRequests();
            await fetchPendingCounts();
        } catch (err) {
            console.error('Error rejecting attendance:', err);
            alert(err.response?.data?.error || 'Failed to reject attendance request');
        } finally {
            setProcessing(null);
        }
    };

    const handleApproveFlexi = async (requestId) => {
        const comments = prompt('Add approval comments (optional):');

        try {
            setProcessing(requestId);

            // Find the flexi request details before approving
            const flexiRequest = flexiRequests.find(req => req.id === requestId);

            await flexiService.approveRequest(requestId, comments || '');

            // Backend sends notification to employee automatically

            // Show success message and refresh list
            alert('Flexi component request approved successfully');
            await fetchFlexiRequests();
            await fetchPendingCounts();
        } catch (err) {
            console.error('Error approving flexi request:', err);
            alert(err.response?.data?.error || 'Failed to approve flexi request');
        } finally {
            setProcessing(null);
        }
    };

    const handleRejectFlexi = async (requestId) => {
        const reason = prompt('Please provide a reason for rejection:');
        if (!reason) return;

        const comments = prompt('Add additional comments (optional):');

        try {
            setProcessing(requestId);

            // Find the flexi request details before rejecting
            const flexiRequest = flexiRequests.find(req => req.id === requestId);

            await flexiService.rejectRequest(requestId, reason, comments || '');

            // Backend sends notification to employee automatically

            // Show success message and refresh list
            alert('Flexi component request rejected');
            await fetchFlexiRequests();
            await fetchPendingCounts();
        } catch (err) {
            console.error('Error rejecting flexi request:', err);
            alert(err.response?.data?.error || 'Failed to reject flexi request');
        } finally {
            setProcessing(null);
        }
    };

    const tabs = [
        { id: 'leave', label: 'Leave Approvals', icon: Calendar, count: pendingCounts.leave || leaveRequests.length },
        { id: 'attendance', label: 'Attendance Approvals', icon: Clock, count: pendingCounts.attendance || attendanceRequests.length },
        { id: 'flexi', label: 'Flexi Component Approvals', icon: DollarSign, count: flexiRequests.length }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Team Approvals
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Review and approve requests from your team members
                </p>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                                    isActive
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                                }`}
                            >
                                <Icon className="w-5 h-5" />
                                {tab.label}
                                {tab.count > 0 && (
                                    <span className="px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full">
                                        {tab.count > 99 ? '99+' : tab.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="flex justify-center items-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
                </div>
            )}

            {/* Leave Approvals Tab */}
            {!loading && activeTab === 'leave' && (
                <div className="space-y-4">
                    {leaveRequests.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                            <Calendar className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                            <p className="text-gray-500 dark:text-gray-400">No pending leave requests</p>
                        </div>
                    ) : (
                        leaveRequests.map((request) => (
                            <div
                                key={request.id}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                                {request.employee_name ? request.employee_name.split(' ').map(n => n[0]).join('').toUpperCase() : 'NA'}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    {request.employee_name || 'Unknown Employee'}
                                                </h3>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {request.employee_code || 'N/A'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Leave Type</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {request.leave_type_name || 'N/A'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Duration</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {request.days_requested} {request.days_requested === 1 ? 'day' : 'days'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {formatDate(request.from_date)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {formatDate(request.to_date)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reason</p>
                                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                                {request.reason}
                                            </p>
                                        </div>

                                        {request.emergency_contact && (
                                            <div className="mb-4">
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Emergency Contact</p>
                                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                                    {request.emergency_contact}
                                                </p>
                                            </div>
                                        )}

                                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                            <Clock className="w-4 h-4" />
                                            Submitted on {formatSubmissionTime(request.created_at)}
                                        </div>
                                    </div>

                                    <div className="flex gap-2 ml-4">
                                        <button
                                            onClick={() => handleApproveLeave(request.id)}
                                            disabled={processing === request.id}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {processing === request.id ? (
                                                <Loader className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <CheckCircle className="w-4 h-4" />
                                            )}
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleRejectLeave(request.id)}
                                            disabled={processing === request.id}
                                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {processing === request.id ? (
                                                <Loader className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <XCircle className="w-4 h-4" />
                                            )}
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Attendance Approvals Tab */}
            {!loading && activeTab === 'attendance' && (
                <div className="space-y-4">
                    {attendanceRequests.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                            <Clock className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                            <p className="text-gray-500 dark:text-gray-400">No pending attendance requests</p>
                        </div>
                    ) : (
                        attendanceRequests.map((request) => (
                            <div
                                key={request.id}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className={`w-10 h-10 ${request.type === 'wfh' ? 'bg-blue-600' : 'bg-purple-600'} rounded-full flex items-center justify-center text-white font-semibold text-sm`}>
                                                {request.first_name && request.last_name ? (request.first_name[0] + request.last_name[0]).toUpperCase() : 'NA'}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    {request.type === 'wfh'
                                                        ? (request.employee_name || `${request.first_name || ''} ${request.last_name || ''}`.trim() || 'Unknown Employee')
                                                        : (`${request.first_name || ''} ${request.last_name || ''}`.trim() || 'Unknown Employee')
                                                    }
                                                </h3>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {request.employee_code || 'N/A'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Request Type</p>
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                                    request.type === 'wfh'
                                                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                                                }`}>
                                                    {request.type === 'wfh' ? 'Work From Home' : (request.regularization_type || 'Regularization').replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Date</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {formatDate(request.type === 'wfh' ? request.request_date : request.attendance_date)}
                                                </p>
                                            </div>
                                            {request.type === 'wfh' ? (
                                                <div className="col-span-2">
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reason</p>
                                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                                        {request.reason || 'No reason provided'}
                                                    </p>
                                                </div>
                                            ) : (
                                                <>
                                                    {request.requested_punch_in && (
                                                        <div>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Requested Punch In</p>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {formatTime(request.requested_punch_in)}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {request.requested_punch_out && (
                                                        <div>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Requested Punch Out</p>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {formatTime(request.requested_punch_out)}
                                                            </p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {request.type !== 'wfh' && (
                                            <div className="mb-4">
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reason</p>
                                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                                    {request.reason || 'No reason provided'}
                                                </p>
                                            </div>
                                        )}

                                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                            <Clock className="w-4 h-4" />
                                            Submitted on {formatSubmissionTime(request.created_at)}
                                        </div>
                                    </div>

                                    <div className="flex gap-2 ml-4">
                                        <button
                                            onClick={() => handleApproveAttendance(request)}
                                            disabled={processing === request.id}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {processing === request.id ? (
                                                <Loader className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <CheckCircle className="w-4 h-4" />
                                            )}
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleRejectAttendance(request)}
                                            disabled={processing === request.id}
                                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {processing === request.id ? (
                                                <Loader className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <XCircle className="w-4 h-4" />
                                            )}
                                            Reject
                                        </button>
                                        {/* Review link - opens employee logs in new tab */}
                                        {request.type === 'regularization' && (
                                            <button
                                                onClick={() => {
                                                    const date = request.attendance_date ? toISTDateString(request.attendance_date) : '';
                                                    window.open(`/manager/team/${request.employee_id}?tab=logs&date=${date}`, '_blank');
                                                }}
                                                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg flex items-center gap-1 transition-colors text-sm"
                                                title="View employee activity logs for this date"
                                            >
                                                <Eye className="w-4 h-4" />
                                                Review
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Flexi Component Approvals Tab */}
            {!loading && activeTab === 'flexi' && (
                <div className="space-y-4">
                    {flexiRequests.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
                            <DollarSign className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                            <p className="text-gray-500 dark:text-gray-400">No pending flexi component requests</p>
                        </div>
                    ) : (
                        flexiRequests.map((request) => (
                            <div
                                key={request.id}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                                {request.employee_name ? request.employee_name.split(' ').map(n => n[0]).join('').toUpperCase() : 'NA'}
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    {request.employee_name || 'Unknown Employee'}
                                                </h3>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    {request.employee_code || 'N/A'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Component Name</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {request.component_name}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Component Type</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                                    {request.component_type || 'Allowance'}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Amount</p>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {request.currency || 'INR'} {parseFloat(request.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</p>
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                                    request.status === 'pending'
                                                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                }`}>
                                                    {request.status === 'under_review' ? 'Under Review' : 'Pending'}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reason</p>
                                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                                {request.reason || 'No reason provided'}
                                            </p>
                                        </div>

                                        {request.supporting_document_url && (
                                            <div className="mb-4">
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Supporting Document</p>
                                                <a
                                                    href={request.supporting_document_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                                >
                                                    <Download className="w-4 h-4" />
                                                    View Document
                                                </a>
                                            </div>
                                        )}

                                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                            <Clock className="w-4 h-4" />
                                            Submitted on {formatSubmissionTime(request.created_at)}
                                        </div>
                                    </div>

                                    <div className="flex gap-2 ml-4">
                                        <button
                                            onClick={() => handleApproveFlexi(request.id)}
                                            disabled={processing === request.id}
                                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {processing === request.id ? (
                                                <Loader className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <CheckCircle className="w-4 h-4" />
                                            )}
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleRejectFlexi(request.id)}
                                            disabled={processing === request.id}
                                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {processing === request.id ? (
                                                <Loader className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <XCircle className="w-4 h-4" />
                                            )}
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default TeamApprovals;
